import { fetchJSON as fetchAPI } from './client';
import type { OrchestrationDashboard } from './workflow';

const WORKFLOW_BASE = import.meta.env.VITE_WORKFLOW_API ?? 'http://localhost:8106';

export type CommerceProcess = {
  id: string;
  name: string;
  version: string;
  description?: string;
  owner: string;
  modules: string[];
  steps: Array<{
    id: string;
    name: string;
    module_id: string;
    trigger_event: string;
    outcome_event: string;
    compensation_action?: string;
    mode: string;
  }>;
  sla: string;
  dependencies?: string[];
};

export type SagaExecution = {
  id: string;
  process_id: string;
  tenant_id: string;
  status: string;
  current_step: number;
  steps: Array<{ id: string; name: string; module_id: string; status: string; attempts: number }>;
  correlation_id: string;
  created_at: string;
  updated_at: string;
};

export type WorkflowDashboard = {
  dashboard: OrchestrationDashboard;
  processes: number;
  active_sagas: number;
  commerce_modules: number;
};

function fetchWorkflow<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(WORKFLOW_BASE, path, init);
}

export const commerceWorkflowApi = {
  dashboard: (workspaceId?: string) =>
    fetchWorkflow<WorkflowDashboard>(`/api/v1/workflows/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  processRegistry: (moduleId?: string) =>
    fetchWorkflow<{ processes: CommerceProcess[]; modules?: string[] }>(
      `/api/v1/process-registry${moduleId ? `?module_id=${moduleId}` : ''}`,
    ),

  listWorkflows: (workspaceId?: string) =>
    fetchWorkflow<{ workflows: unknown[]; processes: CommerceProcess[] }>(
      `/api/v1/workflows${workspaceId ? `?workspace_id=${workspaceId}` : ''}`,
    ),

  getProcess: (id: string) => fetchWorkflow<CommerceProcess>(`/api/v1/workflows/${id}`),

  executeProcess: async (processId: string, body?: Record<string, unknown>) => {
    const res = await fetchWorkflow<{ data: SagaExecution }>(`/api/v1/workflows/${processId}/execute`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
    return res.data ?? (res as unknown as SagaExecution);
  },

  history: (limit = 50) =>
    fetchWorkflow<{ executions: unknown[]; sagas: SagaExecution[] }>(`/api/v1/workflows/history?limit=${limit}`),

  listSagas: () =>
    fetchWorkflow<{ sagas: SagaExecution[]; definitions: CommerceProcess[]; count: number }>('/api/v1/sagas'),
};
